/**
 * Homepage: sport discovery menu — sits after LayeredExplainer, before the
 * Data Coach banner. Six links out to /sports/:slug.
 *
 * Design: an "index" rather than cards — numbered rows on hairline rules,
 * each sport with its own theme-token colour. Hover/focus: a colour wash
 * sweeps in from the left, the emoji ring lights up and tilts, the arrow
 * slides out. Rows cascade in once when the section scrolls into view.
 *
 * Progressive enhancement: plain <a href> links, fully visible without JS;
 * the reveal only arms when JS runs and prefers-reduced-motion isn't set.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SPORTS } from './sportsData';

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const COLORS = ['accent', 'teal', 'green', 'amber', 'purple', 'accentD'];

export default function SportsMenu({ D }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [armed, setArmed] = useState(false); // hidden-until-reveal state
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current) return undefined;
    const mm = window.matchMedia;
    if ((mm && mm('(prefers-reduced-motion: reduce)').matches) || !('IntersectionObserver' in window)) return undefined;
    setArmed(true);
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.2 });
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, []);

  const go = (e, slug) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(`/sports/${slug}`);
    window.scrollTo(0, 0);
  };

  return (
    <section
      ref={rootRef}
      className={`zsm${armed ? ' zsm-armed' : ''}${shown ? ' zsm-shown' : ''}`}
      aria-labelledby="zsm-heading"
      style={{
        '--bg0': D.bg0, '--bg1': D.bg1, '--bg2': D.bg2, '--border': D.border,
        '--text': D.text, '--dim': D.dim, '--accent': D.accent, '--teal': D.teal || D.accent, fontFamily: D.font,
      }}
    >
      <style>{CSS}</style>
      <div className="zsm-wrap">
        <header className="zsm-head">
          <span className="zsm-kicker">Your sport, in depth</span>
          <h2 id="zsm-heading" className="zsm-title">
            Read how ZenAnalytics supports <span className="zsm-em">your game</span>
          </h2>
        </header>

        <ul className="zsm-list">
          {SPORTS.map((s, i) => (
            <li key={s.slug} style={{ '--c': D[COLORS[i]] || D.accent, '--i': i }}>
              <a className="zsm-link" href={`/sports/${s.slug}`} onClick={(e) => go(e, s.slug)}>
                <span className="zsm-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                <span className="zsm-ico" aria-hidden="true">{s.emoji}</span>
                <span className="zsm-text">
                  <span className="zsm-name">{s.name}</span>
                  <span className="zsm-tag">{s.tagline}</span>
                </span>
                <span className="zsm-arrow" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round" focusable="false"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const CSS = `
.zsm { background: var(--bg0); color: var(--text); padding: 36px 16px 44px; }
.zsm *, .zsm *::before, .zsm *::after { box-sizing: border-box; }
.zsm-wrap { max-width: 1120px; margin: 0 auto; }
.zsm-head { margin: 0 0 18px; }
.zsm-kicker { display: block; font-family: ${MONO}; font-size: 11px; letter-spacing: .14em; text-transform: uppercase;
  color: var(--accent); margin-bottom: 8px; }
.zsm-title { margin: 0; font-size: clamp(21px, 4.6vw, 30px); line-height: 1.15; font-weight: 800; letter-spacing: -.02em; max-width: 24ch; }
.zsm-em { background: linear-gradient(90deg, var(--accent), var(--teal)); -webkit-background-clip: text;
  background-clip: text; color: transparent; }

.zsm-list { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.zsm-link { position: relative; isolation: isolate; overflow: hidden; display: flex; flex-direction: column; align-items: flex-start;
  gap: 8px; height: 100%; padding: 14px 14px 13px; border-radius: 14px; text-decoration: none; color: var(--text);
  background: var(--bg1); border: 1px solid var(--border); -webkit-tap-highlight-color: transparent;
  transition: border-color .3s, transform .3s cubic-bezier(.2,.7,.2,1); }
.zsm-link::before { content: ""; position: absolute; inset: 0; z-index: -1; transform: scaleX(0); transform-origin: left;
  background: linear-gradient(90deg, color-mix(in srgb, var(--c) 16%, transparent), color-mix(in srgb, var(--c) 3%, transparent));
  transition: transform .45s cubic-bezier(.2,.7,.2,1); }
.zsm-num { display: none; font-family: ${MONO}; font-size: 11px; letter-spacing: .1em; color: var(--dim); transition: color .3s; }
.zsm-ico { width: 40px; height: 40px; flex: none; border-radius: 50%; display: grid; place-items: center; font-size: 20px; line-height: 1;
  background: color-mix(in srgb, var(--c) 10%, var(--bg2)); border: 1.5px solid color-mix(in srgb, var(--c) 35%, var(--border));
  transition: transform .4s cubic-bezier(.3,1.5,.5,1), box-shadow .4s, border-color .3s; }
.zsm-text { display: flex; flex-direction: column; min-width: 0; gap: 3px; }
.zsm-name { font-size: 15.5px; font-weight: 750; letter-spacing: -.005em; }
.zsm-tag { display: none; font-size: 13.5px; line-height: 1.4; color: var(--dim); }
.zsm-arrow { position: absolute; right: 12px; top: 16px; color: var(--c); opacity: .55;
  transition: transform .35s cubic-bezier(.2,.7,.2,1), opacity .3s; }

.zsm-link:hover, .zsm-link:focus-visible { border-color: color-mix(in srgb, var(--c) 55%, var(--border)); outline: none; }
.zsm-link:focus-visible { box-shadow: 0 0 0 2px var(--c); }
.zsm-link:hover::before, .zsm-link:focus-visible::before { transform: scaleX(1); }
.zsm-link:hover .zsm-ico, .zsm-link:focus-visible .zsm-ico { transform: scale(1.1) rotate(-12deg); border-color: var(--c);
  box-shadow: 0 0 0 5px color-mix(in srgb, var(--c) 16%, transparent), 0 8px 22px -6px color-mix(in srgb, var(--c) 60%, transparent); }
.zsm-link:hover .zsm-num, .zsm-link:focus-visible .zsm-num { color: var(--c); }
.zsm-link:hover .zsm-arrow, .zsm-link:focus-visible .zsm-arrow { opacity: 1; transform: translateX(4px); }
.zsm-link:active { transform: scale(.985); }

/* reveal cascade (only when armed by JS + motion allowed) */
.zsm-armed .zsm-list li { opacity: 0; transform: translateY(12px); }
.zsm-armed.zsm-shown .zsm-list li { opacity: 1; transform: none;
  transition: opacity .5s, transform .55s cubic-bezier(.2,.7,.2,1); transition-delay: calc(var(--i) * 60ms); }

@media (min-width: 640px) {
  .zsm-list { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 860px) {
  .zsm { padding: 52px 24px 60px; }
  .zsm-head { margin-bottom: 24px; }
  .zsm-list { gap: 0 28px; }
  .zsm-list li { border-top: 1px solid var(--border); }
  .zsm-list li:nth-last-child(-n+3) { border-bottom: 1px solid var(--border); }
  .zsm-link { flex-direction: row; align-items: center; gap: 14px; padding: 18px 44px 18px 12px; border: 0; border-radius: 0; background: none; }
  .zsm-link:focus-visible { box-shadow: inset 0 0 0 2px var(--c); }
  .zsm-link:active { transform: none; }
  .zsm-num { display: block; width: 18px; flex: none; }
  .zsm-ico { width: 44px; height: 44px; font-size: 21px; }
  .zsm-name { font-size: 17px; }
  .zsm-tag { display: block; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .zsm-arrow { top: 50%; margin-top: -9px; right: 14px; }
}

@media (prefers-reduced-motion: reduce) {
  .zsm *, .zsm *::before, .zsm *::after { animation: none !important; transition: none !important; }
}
`;
